import React from 'react'
import PropTypes from 'prop-types'
import { Box, Card, CardContent, Grid, Tooltip, Typography } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import { orderBy } from 'lodash'
import { CardTitle } from 'common/components/card/Card'
import { formatMilliers, truncateString } from 'common/utils/StringUtil'
import { darkBlue } from 'app/theme'
import { LOT_STATUS, STATUT_ANNULE, STATUT_FACTURABLE, STATUT_FACTURE } from './LotsUtils'

const sumTotal = (lots) => lots.reduce((acc, l) => acc + (l.total ?? 0), 0)

// Factures à émettre : facturation de l'avancement (budget × avancement), hors lots déjà facturés.
const faeOf = (lot) => (lot.total ?? 0) * (lot.avancement ?? 0) / 100

const CLIENT_NAME_MAX_LENGTH = 20

const clientLabel = (lot) => lot.clientMnemonic || (lot.clientName && truncateString(lot.clientName, CLIENT_NAME_MAX_LENGTH))

const FaeDetail =({ lots, fae }) => {
    const contributions = orderBy(lots.filter(l => faeOf(l) > 0), faeOf, 'desc')
    return (
        <Box sx={{ fontSize: 12 }}>
            <Box sx={{ fontWeight: 700, mb: 0.5 }}>Budget × avancement, hors lots facturés et annulés</Box>
            {contributions.map(l => (
                <Box key={l.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <span>
                        {clientLabel(l) && <b>{`${clientLabel(l)} · `}</b>}
                        {truncateString(l.nom, 40)}
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>{`${formatMilliers(l.total)} € × ${l.avancement}% = ${formatMilliers(Math.round(faeOf(l)))} €`}</span>
                </Box>
            ))}
            <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.5)', mt: 0.5, pt: 0.5, fontWeight: 700, textAlign: 'right' }}>{`Total : ${formatMilliers(fae)} €`}</Box>
        </Box>
    )
}
FaeDetail.propTypes = { lots: PropTypes.arrayOf(PropTypes.shape({})), fae: PropTypes.number }

const StatTile = ({ label, value, tooltip = '' }) => (
    <Tooltip title={tooltip} slotProps={{ tooltip: { sx: { maxWidth: 'none' } } }}>
        <Box sx={{ border: '1px solid #eef1f5', borderRadius: '3px', p: '6px 10px' }}>
            <Typography sx={{ fontSize: 11, color: '#6b7893', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1c2340' }}>{value}</Typography>
        </Box>
    </Tooltip>
)
StatTile.propTypes = { label: PropTypes.string, value: PropTypes.string, tooltip: PropTypes.node }

// Encart de stats sur les lots filtrés : indicateurs chiffrés + répartition par statut.
const LotsStatsCard = ({ lots = [] }) => {
    const activeLots = lots.filter(l => l.statut !== STATUT_ANNULE)
    const billed = sumTotal(activeLots.filter(l => l.statut === STATUT_FACTURE))
    const billable = sumTotal(activeLots.filter(l => l.statut === STATUT_FACTURABLE))
    const faeLots = activeLots.filter(l => l.statut !== STATUT_FACTURE)
    const fae = Math.round(faeLots.reduce((acc, l) => acc + faeOf(l), 0))
    const total = sumTotal(activeLots)

    const pieData = LOT_STATUS
        .map(s => ({ name: s.label, value: lots.filter(l => l.statut === s.value).length, itemStyle: { color: s.color } }))
        .filter(d => d.value > 0)
    const options = {
        tooltip: { trigger: 'item', formatter: '{b} : {c} ({d}%)' },
        legend: { orient: 'vertical', right: 0, top: 'middle', itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 11 } },
        series: [{
            type: 'pie',
            radius: ['45%', '80%'],
            center: ['35%', '50%'],
            data: pieData,
            label: { show: true, position: 'inside', formatter: '{c}', fontSize: 11, fontWeight: 'bold', color: '#fff' },
        }],
    }

    return (
        <Card sx={{ height: '100%' }}>
            <CardTitle title='Statistiques' color={darkBlue} />
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={6} container spacing={1}>
                        <Grid size={6}><StatTile label='Total HT' value={`${formatMilliers(total)} €`} /></Grid>
                        <Grid size={6}><StatTile label='FAE' value={`${formatMilliers(fae)} €`} tooltip={<FaeDetail lots={faeLots} fae={fae} />} /></Grid>
                        <Grid size={6}><StatTile label='Facturable' value={`${formatMilliers(billable)} €`} /></Grid>
                        <Grid size={6}><StatTile label='Autre' value={`${formatMilliers(total - billed - billable)} €`} /></Grid>
                    </Grid>
                    <Grid size={6}>
                        <ReactECharts option={options} notMerge={true} lazyUpdate={true} style={{ height: '150px' }} />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
LotsStatsCard.propTypes = { lots: PropTypes.arrayOf(PropTypes.shape({})) }

export default LotsStatsCard
